import { useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Modal, StatusPill, EmptyState, useToast } from '../../../design-system';

const EMPTY = { name: '', role: '', email: '', phone: '', isPrimary: false };

// Add / edit / remove contacts against the existing
// /api/clients/:id/contacts sub-routes. Parent owns the array and is handed
// the server's updated copy via onChange.
function ContactManager({ clientId, contacts, onChange }) {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // contact _id or null (=add)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openAdd = () => { setEditing(null); setForm(EMPTY); setError(''); setModalOpen(true); };
  const openEdit = (c) => {
    setEditing(c._id);
    setForm({ name: c.name || '', role: c.role || '', email: c.email || '', phone: c.phone || '', isPrimary: !!c.isPrimary });
    setError('');
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const res = editing
        ? await apiClient.put(`/api/clients/${clientId}/contacts/${editing}`, form)
        : await apiClient.post(`/api/clients/${clientId}/contacts`, form);
      onChange(res.data);
      setModalOpen(false);
      toast.success(editing ? 'Contact updated' : 'Contact added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (contactId) => {
    if (!window.confirm('Remove this contact?')) return;
    try {
      const res = await apiClient.delete(`/api/clients/${clientId}/contacts/${contactId}`);
      onChange(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove contact');
    }
  };

  return (
    <Card>
      <Card.Header title="Contacts" action={<Button size="sm" onClick={openAdd}>+ Add Contact</Button>} />
      {contacts.length === 0 ? (
        <EmptyState icon="👤" title="No contacts added" />
      ) : (
        <div className="contacts-list">
          {contacts.map((c) => (
            <div key={c._id} className="contact-card">
              <div className="contact-info">
                <div className="contact-name-row">
                  <strong>{c.name}</strong>
                  {c.isPrimary && <StatusPill variant="success" label="Primary" />}
                </div>
                <div className="contact-meta">
                  {c.role && <span>{c.role}</span>}
                  {c.email && <span>{c.email}</span>}
                  {c.phone && <span>{c.phone}</span>}
                </div>
              </div>
              <div className="enrollment-actions">
                <Button variant="secondary" size="sm" onClick={() => openEdit(c)}>Edit</Button>
                <button onClick={() => remove(c._id)} className="btn-remove">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Contact' : 'Add Contact'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>{editing ? 'Save' : 'Add'}</Button>
          </>
        }
      >
        <form className="client-form" onSubmit={save}>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="form-row">
            <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
          </div>
          <div className="form-row">
            <Input label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={form.isPrimary} onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })} />
            Primary contact
          </label>
        </form>
      </Modal>
    </Card>
  );
}

export default ContactManager;
